import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { AppLayout } from '../components/AppLayout';
import { AddressAutocomplete } from '../components/AddressAutocomplete';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { 
  Trophy,
  PlusCircle,
  Search,
  Filter,
  Download,
  Eye,
  Edit,
  Trash2,
  Loader2,
  MapPin,
  Phone,
  User,
  Building2,
  MessageSquare,
  Clock,
  CheckCircle2,
  XCircle,
  Calendar,
  Mail,
  FileSpreadsheet,
  ChevronRight,
  RefreshCw,
  Settings,
  Send,
  FileText,
  Users,
  Bell,
  AlarmClock,
  TrendingUp,
  AlertTriangle,
  Timer,
  ChevronDown,
  ChevronUp,
  BarChart3
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Retailer options
const RETAILER_OPTIONS = ["Intermarché", "Leclerc", "Système U", "Carrefour", "Auchan"];

// Status configuration
const STATUS_CONFIG = {
  a_traiter: { label: "À traiter", color: "bg-orange-tint text-orange-tint-text", icon: Clock },
  pris_en_compte: { label: "Pris en compte", color: "bg-surface-2 text-[#2E2D2A]", icon: Eye },
  rdv_pris: { label: "RDV pris", color: "bg-graphite-900 text-white", icon: Calendar },
  signe: { label: "Dossier signé", color: "bg-graphite-900 text-white", icon: CheckCircle2 },
  non_signe: { label: "Dossier non signé", color: "bg-surface-2 text-[#2E2D2A]", icon: XCircle }
};

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || { label: status, color: "bg-gray-100 text-gray-800", icon: Clock };
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

const formatCurrency = (value) => {
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  }).format(value || 0);
};

const safeFormatDate = (dateString, formatStr, locale) => {
  if (!dateString) return 'N/A';
  try {
    const date = parseISO(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    return format(date, formatStr, { locale });
  } catch (e) {
    return 'N/A';
  }
};

export default function GoldenInfoPage() {
  const { user, token, isAdmin } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const locale = language === 'fr' ? fr : enUS;

  // State
  const [loading, setLoading] = useState(true);
  const [infos, setInfos] = useState([]);
  const [stats, setStats] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showRecipientsModal, setShowRecipientsModal] = useState(false);
  const [selectedInfo, setSelectedInfo] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [retailerFilter, setRetailerFilter] = useState('all');
  
  // Form state for create
  const [formData, setFormData] = useState({
    retailer: '',
    address: '',
    postal_code: '',
    city: '',
    contact_name: '',
    contact_function: '',
    phone: '',
    comments: '',
    informateur_id: '',
    informateur_name: ''
  });
  
  // Form validation errors
  const [formErrors, setFormErrors] = useState({});
  
  // Users list for informateur dropdown
  const [informateurUsers, setInformateurUsers] = useState([]);
  
  // Form state for status update
  const [statusData, setStatusData] = useState({
    status: '',
    ca_previsionnel: '',
    contract_months: '',
    commission_rate: ''
  });
  
  // Recipients state
  const [recipients, setRecipients] = useState([]);
  const [newRecipient, setNewRecipient] = useState('');

  // Send modal state
  const [showSendModal, setShowSendModal] = useState(false);
  const [usersList, setUsersList] = useState([]);
  const [sendData, setSendData] = useState({
    recipient_user_id: '',
    recipient_email: '',
    recipient_name: '',
    include_pdf: true,
    message: '',
    sendType: 'user' // 'user' or 'email'
  });

  // Reminder settings state
  const [showReminderSettings, setShowReminderSettings] = useState(false);
  const [reminderSettings, setReminderSettings] = useState({
    enabled: true,
    reminder_days: 7,
    email_subject: '',
    email_template: ''
  });
  const [remindingId, setRemindingId] = useState(null); // Track which info is being reminded
  const [reminderStats, setReminderStats] = useState(null);
  const [showDashboard, setShowDashboard] = useState(false);

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const headers = { 'Authorization': `Bearer ${token}` };
      
      const [infosRes, statsRes] = await Promise.all([
        fetch(`${API_URL}/api/golden-infos`, { headers }),
        fetch(`${API_URL}/api/golden-infos/stats`, { headers })
      ]);
      
      if (infosRes.ok) {
        const data = await infosRes.json();
        setInfos(data);
      }
      
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecipients = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/golden-info-recipients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setRecipients(data.emails || []);
      }
    } catch (error) {
      console.error('Error fetching recipients:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/users/list`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setUsersList(data);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchInformateurUsers = async () => {
    try {
      const res = await fetch(`${API_URL}/api/golden-infos/users`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInformateurUsers(data);
      }
    } catch (error) {
      console.error('Error fetching informateur users:', error);
    }
  };

  // Fetch reminder settings (admin only)
  const fetchReminderSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/golden-infos/reminder-settings`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReminderSettings(data);
      }
    } catch (error) {
      console.error('Error fetching reminder settings:', error);
    }
  };

  // Save reminder settings
  const saveReminderSettings = async () => {
    try {
      setSubmitting(true);
      const res = await fetch(`${API_URL}/api/golden-infos/reminder-settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(reminderSettings)
      });
      if (res.ok) {
        toast.success('Paramètres de relance sauvegardés');
        setShowReminderSettings(false);
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  // Fetch reminder stats
  const fetchReminderStats = async () => {
    try {
      const res = await fetch(`${API_URL}/api/golden-infos/reminder-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setReminderStats(data);
      }
    } catch (error) {
      console.error('Error fetching reminder stats:', error);
    }
  };

  // Send manual reminder
  const sendReminder = async (infoId) => {
    try {
      setRemindingId(infoId);
      const res = await fetch(`${API_URL}/api/golden-infos/${infoId}/remind`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`Relance envoyée à ${data.recipient}`);
        fetchData();
        fetchReminderStats();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi de la relance');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'envoi de la relance');
    } finally {
      setRemindingId(null);
    }
  };

  useEffect(() => {
    fetchData();
    fetchUsers();
    fetchInformateurUsers();
    fetchReminderStats();
    if (isAdmin) {
      fetchRecipients();
      fetchReminderSettings();
    }
  }, [token]);

  // Filter infos
  const filteredInfos = useMemo(() => {
    return infos.filter(info => {
      const matchesSearch = searchTerm === '' || 
        info.retailer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        info.city?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        info.contact_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        info.address?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        info.informateur_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        info.creator_name?.toLowerCase().includes(searchTerm.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || info.status === statusFilter;
      const matchesRetailer = retailerFilter === 'all' || info.retailer === retailerFilter;
      
      return matchesSearch && matchesStatus && matchesRetailer;
    });
  }, [infos, searchTerm, statusFilter, retailerFilter]);

  // Create new golden info
  const handleCreate = async (e) => {
    if (e) e.preventDefault();
    
    // Validate required fields
    const errors = {};
    if (!formData.retailer) errors.retailer = true;
    if (!formData.address) errors.address = true;
    if (!formData.postal_code) errors.postal_code = true;
    if (!formData.city) errors.city = true;
    if (!formData.contact_name) errors.contact_name = true;
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      const missingFields = [];
      if (errors.retailer) missingFields.push('Enseigne');
      if (errors.address) missingFields.push('Adresse');
      if (errors.postal_code) missingFields.push('Code postal');
      if (errors.city) missingFields.push('Ville');
      if (errors.contact_name) missingFields.push('Nom du contact');
      toast.error(`Champs obligatoires manquants: ${missingFields.join(', ')}`);
      return;
    }
    
    setFormErrors({});
    
    try {
      setSubmitting(true);
      const res = await fetch(`${API_URL}/api/golden-infos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });
      
      if (res.ok) {
        toast.success('Info d\'Or créée avec succès !');
        setShowCreateModal(false);
        setFormData({
          retailer: '',
          address: '',
          postal_code: '',
          city: '',
          contact_name: '',
          contact_function: '',
          phone: '',
          comments: '',
          informateur_id: '',
          informateur_name: ''
        });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la création');
      }
    } catch (error) {
      console.error('Error creating golden info:', error);
      toast.error('Erreur lors de la création');
    } finally {
      setSubmitting(false);
    }
  };

  // Update status
  const handleStatusUpdate = async (e) => {
    e.preventDefault();
    
    if (!statusData.status) {
      toast.error('Veuillez sélectionner un statut');
      return;
    }
    
    // Validate signed fields
    if (statusData.status === 'signe') {
      if (!statusData.ca_previsionnel || !statusData.contract_months || !statusData.commission_rate) {
        toast.error('Pour le statut "Dossier signé", tous les champs sont obligatoires');
        return;
      }
    }
    
    try {
      setSubmitting(true);
      const payload = {
        status: statusData.status
      };
      
      if (statusData.status === 'signe') {
        payload.ca_previsionnel = parseFloat(statusData.ca_previsionnel);
        payload.contract_months = parseInt(statusData.contract_months);
        payload.commission_rate = parseFloat(statusData.commission_rate) / 100; // Convert to decimal
      }
      
      const res = await fetch(`${API_URL}/api/golden-infos/${selectedInfo.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        toast.success('Statut mis à jour avec succès !');
        setShowStatusModal(false);
        setSelectedInfo(null);
        setStatusData({ status: '', ca_previsionnel: '', contract_months: '', commission_rate: '' });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setSubmitting(false);
    }
  };

  // Update recipients
  const handleSaveRecipients = async () => {
    try {
      setSubmitting(true);
      const res = await fetch(`${API_URL}/api/admin/golden-info-recipients`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ emails: recipients })
      });
      
      if (res.ok) {
        toast.success('Destinataires mis à jour');
        setShowRecipientsModal(false);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSubmitting(false);
    }
  };

  // Export to Excel
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (retailerFilter !== 'all') params.append('retailer', retailerFilter);
      
      const res = await fetch(`${API_URL}/api/golden-infos/export/excel?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Info_dOr_Export_${format(new Date(), 'yyyyMMdd_HHmmss')}.xlsx`;
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('Export réussi');
      } else {
        toast.error('Erreur lors de l\'export');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'export');
    }
  };

  // Delete info (admin only)
  const handleDelete = async (infoId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette Info d\'Or ?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/golden-infos/${infoId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        toast.success('Info d\'Or supprimée');
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de la suppression');
      }
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const openStatusModal = (info) => {
    setSelectedInfo(info);
    setStatusData({
      status: info.status,
      ca_previsionnel: info.ca_previsionnel || '',
      contract_months: info.contract_months || '',
      commission_rate: info.commission_rate ? (info.commission_rate * 100).toFixed(1) : ''
    });
    setShowStatusModal(true);
  };

  const openDetailModal = (info) => {
    setSelectedInfo(info);
    setShowDetailModal(true);
  };

  const openSendModal = (info) => {
    setSelectedInfo(info);
    setSendData({
      recipient_user_id: '',
      recipient_email: '',
      recipient_name: '',
      include_pdf: true,
      message: '',
      sendType: 'user'
    });
    setShowSendModal(true);
  };

  // Download PDF
  const handleDownloadPdf = async (info) => {
    try {
      const res = await fetch(`${API_URL}/api/golden-infos/${info.id}/pdf`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (res.ok) {
        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Info_dOr_${info.retailer}_${info.city}.pdf`.replace(/ /g, '_');
        a.click();
        window.URL.revokeObjectURL(url);
        toast.success('PDF téléchargé');
      } else {
        toast.error('Erreur lors du téléchargement');
      }
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  // Send to user
  const handleSend = async (e) => {
    e.preventDefault();
    
    if (sendData.sendType === 'user' && !sendData.recipient_user_id) {
      toast.error('Veuillez sélectionner un destinataire');
      return;
    }
    
    if (sendData.sendType === 'email' && !sendData.recipient_email) {
      toast.error('Veuillez saisir une adresse email');
      return;
    }
    
    try {
      setSubmitting(true);
      
      const payload = {
        include_pdf: sendData.include_pdf,
        message: sendData.message || null
      };
      
      if (sendData.sendType === 'user') {
        payload.recipient_user_id = sendData.recipient_user_id;
      } else {
        payload.recipient_email = sendData.recipient_email;
        payload.recipient_name = sendData.recipient_name || sendData.recipient_email;
      }
      
      const res = await fetch(`${API_URL}/api/golden-infos/${selectedInfo.id}/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message || 'Info d\'Or envoyée !');
        setShowSendModal(false);
        fetchData(); // Refresh to show updated recipient info
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Error sending:', error);
      toast.error('Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-label mb-1">Commerce · Pipeline des opportunités terrain</p>
            <h1 className="font-display font-bold uppercase leading-[1.05] tracking-[0.01em] text-ink text-4xl sm:text-5xl">Info d'Or</h1>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {user?.role === 'super_admin' && (
              <Button
                variant="outline"
                onClick={() => setShowRecipientsModal(true)}
                className="flex items-center gap-2"
                data-testid="recipients-config-btn"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Destinataires</span>
              </Button>
            )}
            {isAdmin && (
              <Button
                variant="outline"
                onClick={() => { fetchReminderSettings(); setShowReminderSettings(true); }}
                className="flex items-center gap-2"
                data-testid="reminder-settings-btn"
              >
                <AlarmClock className="w-4 h-4" />
                <span className="hidden sm:inline">Relances</span>
              </Button>
            )}
            <Button
              variant="outline"
              onClick={handleExport}
              className="flex items-center gap-2"
              data-testid="export-excel-btn"
            >
              <FileSpreadsheet className="w-4 h-4" />
              <span className="hidden sm:inline">Exporter</span>
            </Button>
            {user?.role !== 'prestataire' && (
              <Button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2"
                data-testid="create-golden-info-btn"
              >
                <PlusCircle className="w-4 h-4" />
                Nouvelle Info
              </Button>
            )}
          </div>
        </div>

        {/* Barre de statuts — bloc scène graphite (6 cellules cliquables) */}
        {stats && (
          <div className="rounded-scene bg-graphite-900 p-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6" data-testid="golden-status-bar">
            {[
              { key: 'all', label: 'Total', value: stats.total },
              { key: 'a_traiter', label: 'À traiter', value: stats.a_traiter },
              { key: 'pris_en_compte', label: 'Pris en compte', value: stats.pris_en_compte },
              { key: 'rdv_pris', label: 'RDV pris', value: stats.rdv_pris },
              { key: 'signe', label: 'Signés', value: stats.signe },
              { key: 'ca', label: 'CA signé', value: formatCurrency(stats.signe * 200), display: true },
            ].map((cell, i) => {
              const active = !cell.display && statusFilter === cell.key;
              const clickable = !cell.display;
              return (
                <button
                  key={cell.key}
                  type="button"
                  disabled={!clickable}
                  onClick={() => clickable && setStatusFilter(cell.key)}
                  className={`text-left px-5 py-2 transition-colors ${i > 0 ? 'lg:border-l border-graphite-600' : ''} ${active ? 'bg-orange rounded-ctrl' : clickable ? 'hover:bg-graphite-700 rounded-ctrl' : ''}`}
                  data-testid={`golden-stat-${cell.key}`}
                >
                  <p className={`text-[11px] font-bold uppercase tracking-[0.12em] mb-1 ${active ? 'text-orange-on/80' : 'text-ondark-3'}`}>{cell.label}</p>
                  <p className={`font-display font-semibold tabular-nums leading-none ${cell.display ? 'text-[26px]' : 'text-[40px]'} ${active ? 'text-orange-on' : 'text-white'}`}>{cell.value}</p>
                </button>
              );
            })}
          </div>
        )}

        {/* Reminder Dashboard - Collapsible */}
        {reminderStats && (
          <Card className="border-slate-200 dark:border-slate-700" data-testid="reminder-dashboard">
            <button
              onClick={() => setShowDashboard(!showDashboard)}
              className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors rounded-t-lg"
              data-testid="toggle-dashboard-btn"
            >
              <div className="flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-amber-500" />
                <span className="font-semibold text-slate-800 dark:text-slate-200">
                  Dashboard Relances & Conversion
                </span>
              </div>
              {showDashboard ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
            </button>
            
            {showDashboard && (
              <CardContent className="pt-0 pb-5 px-5 space-y-5">
                {/* KPI Row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {/* Total Reminders */}
                  <div className="rounded-xl bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 p-4" data-testid="kpi-total-reminders">
                    <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400 mb-2">
                      <Bell className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase tracking-wide">Relances envoyées</span>
                    </div>
                    <p className="text-3xl font-bold text-amber-900 dark:text-amber-100">{reminderStats.total_reminders_sent}</p>
                  </div>
                  
                  {/* Conversion Rate */}
                  <div className="rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border border-green-200 dark:border-green-800 p-4" data-testid="kpi-conversion-rate">
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400 mb-2">
                      <TrendingUp className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase tracking-wide">Taux conversion</span>
                    </div>
                    <p className="text-3xl font-bold text-green-900 dark:text-green-100">{reminderStats.conversion_rate}%</p>
                    <p className="text-xs text-green-600 dark:text-green-500 mt-1">
                      {reminderStats.status_breakdown?.signe || 0} signé(s) / {reminderStats.total_infos} total
                    </p>
                  </div>
                  
                  {/* Avg Processing Time */}
                  <div className="rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 p-4" data-testid="kpi-avg-processing">
                    <div className="flex items-center gap-2 text-blue-600 dark:text-blue-400 mb-2">
                      <Timer className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase tracking-wide">Temps moyen</span>
                    </div>
                    <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                      {reminderStats.avg_processing_days > 0 ? `${reminderStats.avg_processing_days}j` : '-'}
                    </p>
                    <p className="text-xs text-blue-600 dark:text-blue-500 mt-1">Création → Clôture</p>
                  </div>
                  
                  {/* Overdue */}
                  <div className={`rounded-xl p-4 border ${reminderStats.overdue_count > 0 
                    ? 'bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-900/20 dark:to-orange-900/20 border-red-200 dark:border-red-800' 
                    : 'bg-gradient-to-br from-slate-50 to-gray-50 dark:from-slate-900/20 dark:to-gray-900/20 border-slate-200 dark:border-slate-700'}`} data-testid="kpi-overdue">
                    <div className={`flex items-center gap-2 mb-2 ${reminderStats.overdue_count > 0 ? 'text-red-600 dark:text-red-400' : 'text-slate-500 dark:text-slate-400'}`}>
                      <AlertTriangle className="w-4 h-4" />
                      <span className="text-xs font-medium uppercase tracking-wide">En retard</span>
                    </div>
                    <p className={`text-3xl font-bold ${reminderStats.overdue_count > 0 ? 'text-red-900 dark:text-red-100' : 'text-slate-700 dark:text-slate-300'}`}>
                      {reminderStats.overdue_count}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">Délai relance dépassé</p>
                  </div>
                </div>

                {/* Status Pipeline Visualization */}
                <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-4">
                  <h4 className="text-sm font-semibold text-slate-700 dark:text-slate-300 mb-3 flex items-center gap-2">
                    <ChevronRight className="w-4 h-4" />
                    Pipeline par statut
                  </h4>
                  <div className="flex items-center gap-1 h-8 rounded-lg overflow-hidden" data-testid="pipeline-bar">
                    {(() => {
                      const bd = reminderStats.status_breakdown || {};
                      const total = reminderStats.total_infos || 1;
                      const segments = [
                        { key: 'a_traiter', label: 'À traiter', count: bd.a_traiter || 0, color: 'bg-amber-400' },
                        { key: 'pris_en_compte', label: 'Pris en compte', count: bd.pris_en_compte || 0, color: 'bg-blue-400' },
                        { key: 'rdv_pris', label: 'RDV pris', count: bd.rdv_pris || 0, color: 'bg-purple-400' },
                        { key: 'signe', label: 'Signé', count: bd.signe || 0, color: 'bg-green-500' },
                        { key: 'non_signe', label: 'Non signé', count: bd.non_signe || 0, color: 'bg-red-400' },
                      ];
                      return segments.filter(s => s.count > 0).map(s => (
                        <div
                          key={s.key}
                          className={`${s.color} h-full flex items-center justify-center text-white text-xs font-medium transition-all`}
                          style={{ width: `${(s.count / total) * 100}%`, minWidth: s.count > 0 ? '28px' : '0' }}
                          title={`${s.label}: ${s.count}`}
                        >
                          {s.count}
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="flex flex-wrap gap-3 mt-2">
                    {[
                      { label: 'À traiter', color: 'bg-amber-400', count: reminderStats.status_breakdown?.a_traiter },
                      { label: 'Pris en compte', color: 'bg-blue-400', count: reminderStats.status_breakdown?.pris_en_compte },
                      { label: 'RDV pris', color: 'bg-purple-400', count: reminderStats.status_breakdown?.rdv_pris },
                      { label: 'Signé', color: 'bg-green-500', count: reminderStats.status_breakdown?.signe },
                      { label: 'Non signé', color: 'bg-red-400', count: reminderStats.status_breakdown?.non_signe },
                    ].filter(s => (s.count || 0) > 0).map(s => (
                      <span key={s.label} className="flex items-center gap-1 text-xs text-slate-600 dark:text-slate-400">
                        <span className={`w-2.5 h-2.5 rounded-full ${s.color}`} />
                        {s.label} ({s.count})
                      </span>
                    ))}
                  </div>
                </div>

                {/* Overdue List */}
                {reminderStats.overdue_count > 0 && (
                  <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50/50 dark:bg-red-900/10 p-4" data-testid="overdue-list">
                    <h4 className="text-sm font-semibold text-red-700 dark:text-red-400 mb-3 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Info d'Or en retard de relance
                    </h4>
                    <div className="space-y-2">
                      {reminderStats.overdue_infos.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center justify-between p-2.5 bg-white dark:bg-slate-800 rounded-lg border border-red-100 dark:border-red-900"
                          data-testid={`overdue-item-${item.id}`}
                        >
                          <div className="flex items-center gap-3">
                            <div>
                              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
                                {item.retailer} - {item.city}
                              </p>
                              <p className="text-xs text-slate-500">
                                {item.contact_name} {item.last_recipient_name ? `· Dest: ${item.last_recipient_name}` : ''}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge variant="outline" className="text-red-600 border-red-300 text-xs">
                              {item.days_since_last_action}j
                            </Badge>
                            {item.reminder_count > 0 && (
                              <Badge variant="outline" className="text-amber-600 border-amber-300 text-xs">
                                <Bell className="w-3 h-3 mr-1" />{item.reminder_count}
                              </Badge>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => sendReminder(item.id)}
                              disabled={remindingId === item.id}
                              className="text-amber-600 hover:bg-amber-50 h-7 px-2"
                              data-testid={`overdue-remind-btn-${item.id}`}
                            >
                              {remindingId === item.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Bell className="w-3.5 h-3.5" />}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            )}
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Rechercher par enseigne, ville, contact, informateur..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
              
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="status-filter">
                  <SelectValue placeholder="Tous les statuts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Select value={retailerFilter} onValueChange={setRetailerFilter}>
                <SelectTrigger className="w-full sm:w-[180px]" data-testid="retailer-filter">
                  <SelectValue placeholder="Toutes enseignes" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes enseignes</SelectItem>
                  {RETAILER_OPTIONS.map(retailer => (
                    <SelectItem key={retailer} value={retailer}>{retailer}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              <Button variant="ghost" onClick={fetchData} className="flex items-center gap-2" data-testid="refresh-btn">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Enseigne</TableHead>
                    <TableHead>Ville</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Informateur</TableHead>
                    <TableHead>Destinataire</TableHead>
                    <TableHead className="text-center">Relances</TableHead>
                    <TableHead className="text-center">Prime</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredInfos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={10} className="text-center py-8 text-slate-500">
                        Aucune Info d'Or trouvée
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredInfos.map((info) => (
                      <TableRow key={info.id} className="cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`golden-info-row-${info.id}`}>
                        <TableCell className="font-medium text-sm">
                          {safeFormatDate(info.created_at, 'dd/MM/yyyy', locale)}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-slate-400" />
                            <span className="font-medium">{info.retailer}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <MapPin className="w-4 h-4 text-slate-400" />
                            <span>{info.postal_code} {info.city}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <User className="w-4 h-4 text-slate-400" />
                            <span>{info.contact_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <StatusBadge status={info.status} />
                        </TableCell>
                        <TableCell className="text-sm">
                          {isAdmin ? (
                            <Select 
                              value={info.informateur_id || info.created_by} 
                              onValueChange={async (newInformateurId) => {
                                const selectedUser = informateurUsers.find(u => u.id === newInformateurId);
                                if (selectedUser) {
                                  try {
                                    const res = await fetch(`${API_URL}/api/golden-infos/${info.id}`, {
                                      method: 'PUT',
                                      headers: {
                                        'Content-Type': 'application/json',
                                        'Authorization': `Bearer ${token}`
                                      },
                                      body: JSON.stringify({
                                        informateur_id: newInformateurId,
                                        informateur_name: selectedUser.name || selectedUser.email
                                      })
                                    });
                                    if (res.ok) {
                                      toast.success('Informateur mis à jour');
                                      fetchData();
                                    }
                                  } catch (error) {
                                    toast.error('Erreur lors de la mise à jour');
                                  }
                                }
                              }}
                            >
                              <SelectTrigger className="h-8 w-40 text-xs">
                                <SelectValue placeholder={info.informateur_name || info.creator_name} />
                              </SelectTrigger>
                              <SelectContent>
                                {informateurUsers.map(u => (
                                  <SelectItem key={u.id} value={u.id} className="text-xs">
                                    {u.name || u.email}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          ) : (
                            <span className="text-slate-500">{info.informateur_name || info.creator_name}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">
                          {info.last_recipient_name ? (
                            <div className="flex items-center gap-1">
                              <Mail className="w-3 h-3 text-blue-400" />
                              <span className="text-slate-600 dark:text-slate-400" data-testid={`recipient-${info.id}`}>{info.last_recipient_name}</span>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs italic" data-testid={`no-recipient-${info.id}`}>Non envoyé</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          {info.reminder_count > 0 ? (
                            <div className="flex items-center justify-center gap-1">
                              <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">
                                <Bell className="w-3 h-3 mr-1" />
                                {info.reminder_count}
                              </Badge>
                            </div>
                          ) : (
                            <span className="text-slate-400 text-xs">-</span>
                          )}
                        </TableCell>
                        <TableCell className="text-center" data-testid={`prime-${info.id}`}>
                          {info.status === 'signe' ? (
                            <Badge className="bg-green-100 text-green-700 border-green-300 font-semibold">200 €</Badge>
                          ) : (
                            <span className="text-slate-400 text-sm">0 €</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetailModal(info)}
                              data-testid={`view-btn-${info.id}`}
                              title="Voir détails"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadPdf(info)}
                              data-testid={`pdf-btn-${info.id}`}
                              title="Télécharger PDF"
                            >
                              <FileText className="w-4 h-4 text-red-500" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openSendModal(info)}
                              data-testid={`send-btn-${info.id}`}
                              title="Envoyer à un utilisateur"
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                            {/* Reminder button - only for non-final statuses */}
                            {info.status !== 'signe' && info.status !== 'non_signe' && (
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => sendReminder(info.id)}
                                disabled={remindingId === info.id}
                                data-testid={`remind-btn-${info.id}`}
                                title={`Relancer ${info.last_recipient_name || info.informateur_name || info.creator_name}${info.reminder_count ? ` (${info.reminder_count} relances)` : ''}`}
                                className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              >
                                {remindingId === info.id ? (
                                  <Loader2 className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Bell className="w-4 h-4" />
                                )}
                              </Button>
                            )}
                            {isAdmin && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openStatusModal(info)}
                                  data-testid={`status-btn-${info.id}`}
                                  title="Modifier statut"
                                >
                                  <Edit className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDelete(info.id)}
                                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                  data-testid={`delete-btn-${info.id}`}
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Create Modal */}
        <Dialog open={showCreateModal} onOpenChange={setShowCreateModal}>
          <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Nouvelle Info d'Or
              </DialogTitle>
              <DialogDescription>
                Signalez une opportunité commerciale terrain
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleCreate} className="space-y-4 overflow-y-auto flex-1 pr-2">
              <div className="space-y-2">
                <Label htmlFor="retailer" className={formErrors.retailer ? 'text-red-500' : ''}>
                  Enseigne * {formErrors.retailer && <span className="text-xs">(obligatoire)</span>}
                </Label>
                <Select value={formData.retailer} onValueChange={(v) => {
                  setFormData({...formData, retailer: v});
                  setFormErrors(prev => ({...prev, retailer: false}));
                }}>
                  <SelectTrigger 
                    data-testid="create-retailer-select"
                    className={formErrors.retailer ? 'border-red-500 ring-red-500' : ''}
                  >
                    <SelectValue placeholder="Sélectionner une enseigne" />
                  </SelectTrigger>
                  <SelectContent>
                    {RETAILER_OPTIONS.map(r => (
                      <SelectItem key={r} value={r}>{r}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Informateur field */}
              <div className="space-y-2">
                <Label htmlFor="informateur">
                  Informateur
                  <span className="text-xs text-slate-500 ml-2">(par défaut: vous)</span>
                </Label>
                <Select 
                  value={formData.informateur_id} 
                  onValueChange={(v) => {
                    const selectedUser = informateurUsers.find(u => u.id === v);
                    setFormData({
                      ...formData, 
                      informateur_id: v,
                      informateur_name: selectedUser?.name || ''
                    });
                  }}
                >
                  <SelectTrigger data-testid="create-informateur-select">
                    <SelectValue placeholder={user?.name || "Sélectionner un informateur"} />
                  </SelectTrigger>
                  <SelectContent>
                    {informateurUsers.map(u => (
                      <SelectItem key={u.id} value={u.id}>
                        {u.name || u.email} {u.role && <span className="text-slate-400">({u.role})</span>}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {/* Google Places Autocomplete */}
              <div className="space-y-2">
                <AddressAutocomplete
                  language={language}
                  placeholder="Ex: Leclerc Lyon, Intermarché Bordeaux..."
                  onAddressSelect={(data) => {
                    setFormData(prev => ({
                      ...prev,
                      address: data.address,
                      postal_code: data.postal_code || prev.postal_code,
                      city: data.city || prev.city
                    }));
                    setFormErrors(prev => ({...prev, address: false, postal_code: false, city: false}));
                  }}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address" className={formErrors.address ? 'text-red-500' : ''}>
                  Adresse * {formErrors.address && <span className="text-xs">(obligatoire)</span>}
                </Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => {
                    setFormData({...formData, address: e.target.value});
                    setFormErrors(prev => ({...prev, address: false}));
                  }}
                  placeholder="123 Rue du Commerce"
                  data-testid="create-address-input"
                  className={formErrors.address ? 'border-red-500' : ''}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="postal_code" className={formErrors.postal_code ? 'text-red-500' : ''}>
                    Code postal * {formErrors.postal_code && <span className="text-xs">(obligatoire)</span>}
                  </Label>
                  <Input
                    id="postal_code"
                    value={formData.postal_code}
                    onChange={(e) => {
                      setFormData({...formData, postal_code: e.target.value});
                      setFormErrors(prev => ({...prev, postal_code: false}));
                    }}
                    placeholder="75001"
                    data-testid="create-postal-input"
                    className={formErrors.postal_code ? 'border-red-500' : ''}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="city" className={formErrors.city ? 'text-red-500' : ''}>
                    Ville * {formErrors.city && <span className="text-xs">(obligatoire)</span>}
                  </Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => {
                      setFormData({...formData, city: e.target.value});
                      setFormErrors(prev => ({...prev, city: false}));
                    }}
                    placeholder="Paris"
                    data-testid="create-city-input"
                    className={formErrors.city ? 'border-red-500' : ''}
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="contact_name" className={formErrors.contact_name ? 'text-red-500' : ''}>
                  Nom du contact * {formErrors.contact_name && <span className="text-xs">(obligatoire)</span>}
                </Label>
                <Input
                  id="contact_name"
                  value={formData.contact_name}
                  onChange={(e) => {
                    setFormData({...formData, contact_name: e.target.value});
                    setFormErrors(prev => ({...prev, contact_name: false}));
                  }}
                  placeholder="Jean Dupont"
                  data-testid="create-contact-input"
                  className={formErrors.contact_name ? 'border-red-500' : ''}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="contact_function">Fonction</Label>
                  <Input
                    id="contact_function"
                    value={formData.contact_function}
                    onChange={(e) => setFormData({...formData, contact_function: e.target.value})}
                    placeholder="Directeur"
                    data-testid="create-function-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Téléphone</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    placeholder="06 12 34 56 78"
                    data-testid="create-phone-input"
                  />
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="comments">Commentaires</Label>
                <Textarea
                  id="comments"
                  value={formData.comments}
                  onChange={(e) => setFormData({...formData, comments: e.target.value})}
                  placeholder="Informations complémentaires..."
                  rows={3}
                  data-testid="create-comments-input"
                />
              </div>
            </form>
              
            <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
              <Button type="button" variant="outline" onClick={() => {
                setShowCreateModal(false);
                setFormErrors({});
              }}>
                Annuler
              </Button>
              <Button 
                type="button" 
                disabled={submitting} 
                className="bg-gradient-to-r from-amber-500 to-orange-600" 
                data-testid="submit-create-btn"
                onClick={handleCreate}
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trophy className="w-4 h-4 mr-2" />}
                Créer l'Info
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Status Update Modal (Admin only) */}
        <Dialog open={showStatusModal} onOpenChange={setShowStatusModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Modifier le statut</DialogTitle>
              <DialogDescription>
                {selectedInfo?.retailer} - {selectedInfo?.city}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleStatusUpdate} className="space-y-4">
              <div className="space-y-2">
                <Label>Nouveau statut</Label>
                <Select value={statusData.status} onValueChange={(v) => setStatusData({...statusData, status: v})}>
                  <SelectTrigger data-testid="status-select">
                    <SelectValue placeholder="Sélectionner un statut" />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                      <SelectItem key={key} value={key}>{config.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {statusData.status === 'signe' && (
                <>
                  <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800 space-y-4">
                    <p className="text-sm font-medium text-green-800 dark:text-green-300">
                      Informations du dossier signé
                    </p>
                    
                    <div className="space-y-2">
                      <Label htmlFor="ca_previsionnel">CA Prévisionnel (€) *</Label>
                      <Input
                        id="ca_previsionnel"
                        type="number"
                        value={statusData.ca_previsionnel}
                        onChange={(e) => setStatusData({...statusData, ca_previsionnel: e.target.value})}
                        placeholder="50000"
                        data-testid="ca-input"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="contract_months">Durée du contrat (mois) *</Label>
                      <Select value={statusData.contract_months?.toString()} onValueChange={(v) => setStatusData({...statusData, contract_months: v})}>
                        <SelectTrigger data-testid="months-select">
                          <SelectValue placeholder="Sélectionner" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="24">24 mois</SelectItem>
                          <SelectItem value="36">36 mois</SelectItem>
                          <SelectItem value="48">48 mois</SelectItem>
                          <SelectItem value="60">60 mois</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    
                    <div className="space-y-2">
                      <Label htmlFor="commission_rate">Taux (%) *</Label>
                      <Input
                        id="commission_rate"
                        type="number"
                        step="0.1"
                        value={statusData.commission_rate}
                        onChange={(e) => setStatusData({...statusData, commission_rate: e.target.value})}
                        placeholder="3.5"
                        data-testid="rate-input"
                      />
                    </div>
                  </div>
                </>
              )}
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowStatusModal(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={submitting} data-testid="submit-status-btn">
                  {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                  Mettre à jour
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Detail Modal */}
        <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy className="w-5 h-5 text-amber-500" />
                Détails de l'Info d'Or
              </DialogTitle>
            </DialogHeader>
            
            {selectedInfo && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <StatusBadge status={selectedInfo.status} />
                  <span className="text-sm text-slate-500">
                    {safeFormatDate(selectedInfo.created_at, 'dd MMMM yyyy à HH:mm', locale)}
                  </span>
                </div>
                
                <div className="grid gap-4">
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
                    <div className="flex items-center gap-3">
                      <Building2 className="w-5 h-5 text-amber-500" />
                      <div>
                        <p className="text-sm text-slate-500">Enseigne</p>
                        <p className="font-medium">{selectedInfo.retailer}</p>
                      </div>
                    </div>
                    
                    <div className="flex items-start gap-3">
                      <MapPin className="w-5 h-5 text-slate-400 mt-0.5" />
                      <div>
                        <p className="text-sm text-slate-500">Adresse</p>
                        <p className="font-medium">{selectedInfo.address}</p>
                        <p>{selectedInfo.postal_code} {selectedInfo.city}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg space-y-3">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-blue-500" />
                      <div>
                        <p className="text-sm text-slate-500">Contact</p>
                        <p className="font-medium">{selectedInfo.contact_name}</p>
                        {selectedInfo.contact_function && <p className="text-sm text-slate-500">{selectedInfo.contact_function}</p>}
                      </div>
                    </div>
                    
                    {selectedInfo.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-5 h-5 text-green-500" />
                        <div>
                          <p className="text-sm text-slate-500">Téléphone</p>
                          <p className="font-medium">{selectedInfo.phone}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {selectedInfo.comments && (
                    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                      <div className="flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-purple-500 mt-0.5" />
                        <div>
                          <p className="text-sm text-slate-500">Commentaires</p>
                          <p className="whitespace-pre-wrap">{selectedInfo.comments}</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {selectedInfo.status === 'signe' && (
                    <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-800">
                      <p className="text-sm font-medium text-green-800 dark:text-green-300 mb-3">Dossier signé</p>
                      <div className="grid grid-cols-3 gap-4 text-center">
                        <div>
                          <p className="text-xs text-green-600">CA Prév.</p>
                          <p className="font-bold text-green-800 dark:text-green-300">{formatCurrency(selectedInfo.ca_previsionnel)}</p>
                        </div>
                        <div>
                          <p className="text-xs text-green-600">Durée</p>
                          <p className="font-bold text-green-800 dark:text-green-300">{selectedInfo.contract_months} mois</p>
                        </div>
                        <div>
                          <p className="text-xs text-green-600">Taux</p>
                          <p className="font-bold text-green-800 dark:text-green-300">{((selectedInfo.commission_rate || 0) * 100).toFixed(1)}%</p>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex flex-col gap-2 text-sm text-slate-500 pt-2 border-t">
                    <div className="flex items-center justify-between">
                      <span>Informateur: <strong className="text-slate-700 dark:text-slate-300">{selectedInfo.informateur_name || selectedInfo.creator_name}</strong></span>
                    </div>
                    {selectedInfo.last_recipient_name && (
                      <div className="flex items-center justify-between">
                        <span>Dernier destinataire: <strong className="text-blue-700 dark:text-blue-300">{selectedInfo.last_recipient_name}</strong></span>
                        <span className="text-xs">{selectedInfo.last_recipient_email}</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between">
                      <span>Créé par: {selectedInfo.creator_name}</span>
                      <span>{selectedInfo.creator_email}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                Fermer
              </Button>
              {isAdmin && selectedInfo && (
                <Button onClick={() => { setShowDetailModal(false); openStatusModal(selectedInfo); }}>
                  Modifier le statut
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Recipients Configuration Modal (Super Admin only) */}
        <Dialog open={showRecipientsModal} onOpenChange={setShowRecipientsModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Mail className="w-5 h-5 text-blue-500" />
                Destinataires des notifications
              </DialogTitle>
              <DialogDescription>
                Ces adresses recevront les nouvelles Info d'Or
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="flex gap-2">
                <Input
                  type="email"
                  value={newRecipient}
                  onChange={(e) => setNewRecipient(e.target.value)}
                  placeholder="email@example.com"
                  data-testid="new-recipient-input"
                />
                <Button
                  type="button"
                  onClick={() => {
                    if (newRecipient && !recipients.includes(newRecipient)) {
                      setRecipients([...recipients, newRecipient]);
                      setNewRecipient('');
                    }
                  }}
                  data-testid="add-recipient-btn"
                >
                  Ajouter
                </Button>
              </div>
              
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {recipients.length === 0 ? (
                  <p className="text-sm text-slate-500 text-center py-4">Aucun destinataire configuré</p>
                ) : (
                  recipients.map((email, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-slate-50 dark:bg-slate-800 rounded">
                      <span className="text-sm">{email}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setRecipients(recipients.filter((_, i) => i !== idx))}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowRecipientsModal(false)}>
                Annuler
              </Button>
              <Button onClick={handleSaveRecipients} disabled={submitting} data-testid="save-recipients-btn">
                {submitting && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send to User Modal */}
        <Dialog open={showSendModal} onOpenChange={setShowSendModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="w-5 h-5 text-blue-500" />
                Envoyer l'Info d'Or
              </DialogTitle>
              <DialogDescription>
                {selectedInfo?.retailer} - {selectedInfo?.city}
              </DialogDescription>
            </DialogHeader>
            
            <form onSubmit={handleSend} className="space-y-4">
              {/* Send type selection */}
              <div className="flex gap-2 p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
                <button
                  type="button"
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    sendData.sendType === 'user' 
                      ? 'bg-white dark:bg-slate-700 shadow text-blue-600' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                  onClick={() => setSendData({...sendData, sendType: 'user', recipient_email: '', recipient_name: ''})}
                >
                  <Users className="w-4 h-4" />
                  Utilisateur
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    sendData.sendType === 'email' 
                      ? 'bg-white dark:bg-slate-700 shadow text-blue-600' 
                      : 'text-slate-600 hover:text-slate-800'
                  }`}
                  onClick={() => setSendData({...sendData, sendType: 'email', recipient_user_id: ''})}
                >
                  <Mail className="w-4 h-4" />
                  Email externe
                </button>
              </div>
              
              {sendData.sendType === 'user' ? (
                <div className="space-y-2">
                  <Label>Sélectionner un utilisateur *</Label>
                  <Select 
                    value={sendData.recipient_user_id} 
                    onValueChange={(v) => setSendData({...sendData, recipient_user_id: v})}
                  >
                    <SelectTrigger data-testid="send-user-select">
                      <SelectValue placeholder="Choisir un destinataire" />
                    </SelectTrigger>
                    <SelectContent className="max-h-60 overflow-y-auto">
                      {usersList.map(u => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.name || u.email} ({u.role})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label>Email du destinataire *</Label>
                    <Input
                      type="email"
                      value={sendData.recipient_email}
                      onChange={(e) => setSendData({...sendData, recipient_email: e.target.value})}
                      placeholder="destinataire@example.com"
                      data-testid="send-email-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Nom du destinataire</Label>
                    <Input
                      value={sendData.recipient_name}
                      onChange={(e) => setSendData({...sendData, recipient_name: e.target.value})}
                      placeholder="Jean Dupont"
                      data-testid="send-name-input"
                    />
                  </div>
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Message personnel (optionnel)</Label>
                <Textarea
                  value={sendData.message}
                  onChange={(e) => setSendData({...sendData, message: e.target.value})}
                  placeholder="Ajouter un message personnalisé..."
                  rows={3}
                  data-testid="send-message-input"
                />
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <input
                  type="checkbox"
                  id="include_pdf"
                  checked={sendData.include_pdf}
                  onChange={(e) => setSendData({...sendData, include_pdf: e.target.checked})}
                  className="w-4 h-4 text-blue-600 rounded"
                />
                <Label htmlFor="include_pdf" className="flex items-center gap-2 cursor-pointer">
                  <FileText className="w-4 h-4 text-red-500" />
                  Joindre le PDF de l'Info d'Or
                </Label>
              </div>
              
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowSendModal(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={submitting} className="bg-blue-600 hover:bg-blue-700" data-testid="submit-send-btn">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Send className="w-4 h-4 mr-2" />}
                  Envoyer
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Reminder Settings Modal */}
        <Dialog open={showReminderSettings} onOpenChange={setShowReminderSettings}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <AlarmClock className="w-5 h-5 text-amber-500" />
                Paramètres de Relance Automatique
              </DialogTitle>
              <DialogDescription>
                Configurez le système de relance automatique pour les Info d'Or en attente
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-6 overflow-y-auto flex-1 pr-2">
              {/* Enable/Disable */}
              <div className="flex items-center justify-between p-4 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Activer les relances automatiques</Label>
                  <p className="text-sm text-slate-500 mt-1">Envoie automatiquement des emails de relance</p>
                </div>
                <Button
                  variant={reminderSettings.enabled ? "default" : "outline"}
                  onClick={() => setReminderSettings({...reminderSettings, enabled: !reminderSettings.enabled})}
                  className={reminderSettings.enabled ? "bg-green-600 hover:bg-green-700" : ""}
                >
                  {reminderSettings.enabled ? "Activé" : "Désactivé"}
                </Button>
              </div>
              
              {/* Days interval */}
              <div className="space-y-2">
                <Label htmlFor="reminder_days" className="text-base font-medium">
                  Délai entre les relances
                </Label>
                <div className="flex items-center gap-3">
                  <Input
                    id="reminder_days"
                    type="number"
                    min="1"
                    max="90"
                    value={reminderSettings.reminder_days}
                    onChange={(e) => setReminderSettings({...reminderSettings, reminder_days: parseInt(e.target.value) || 7})}
                    className="w-24"
                  />
                  <span className="text-slate-600">jours</span>
                </div>
                <p className="text-sm text-slate-500">
                  Les Info d'Or non clôturées recevront une relance tous les {reminderSettings.reminder_days} jours
                </p>
              </div>
              
              {/* Email Subject */}
              <div className="space-y-2">
                <Label htmlFor="email_subject" className="text-base font-medium">
                  Objet de l'email
                </Label>
                <Input
                  id="email_subject"
                  value={reminderSettings.email_subject}
                  onChange={(e) => setReminderSettings({...reminderSettings, email_subject: e.target.value})}
                  placeholder="🔔 Relance Info d'Or - {retailer} {city}"
                />
                <p className="text-xs text-slate-500">
                  Variables disponibles: {'{retailer}'}, {'{city}'}, {'{contact_name}'}, {'{days_pending}'}
                </p>
              </div>
              
              {/* Email Template */}
              <div className="space-y-2">
                <Label htmlFor="email_template" className="text-base font-medium">
                  Template de l'email
                </Label>
                <Textarea
                  id="email_template"
                  value={reminderSettings.email_template}
                  onChange={(e) => setReminderSettings({...reminderSettings, email_template: e.target.value})}
                  rows={12}
                  className="font-mono text-sm"
                  placeholder="Bonjour {informateur_name},..."
                />
                <p className="text-xs text-slate-500">
                  Variables: {'{informateur_name}'}, {'{retailer}'}, {'{address}'}, {'{postal_code}'}, {'{city}'}, {'{contact_name}'}, {'{contact_function}'}, {'{phone}'}, {'{created_at}'}, {'{status}'}, {'{days_pending}'}
                </p>
              </div>
            </div>
            
            <DialogFooter className="flex-shrink-0 border-t pt-4 mt-4">
              <Button variant="outline" onClick={() => setShowReminderSettings(false)}>
                Annuler
              </Button>
              <Button 
                onClick={saveReminderSettings}
                disabled={submitting}
                className="bg-amber-600 hover:bg-amber-700"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                Enregistrer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}
